/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { FaStar,FaQuoteLeft, FaQuoteRight, FaGem } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import './ProductReviews.css';

const ProductReviews = ({ productId, productName, reviews = [], loadingReviews = false, onReviewSubmitted }) => {
  const { currentUser } = useAuth();
  const [localReviews, setLocalReviews] = useState(reviews);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  // Use reviews passed from parent component instead of fetching separately
  useEffect(() => {
    if (reviews && reviews.length > 0) {
      setLocalReviews(reviews);
    }
  }, [reviews]);

  // Show loading state only when actually fetching, not when there are no reviews
  const isActuallyLoading = loadingReviews;

  const handleStarClick = (starRating) => {
    setRating(starRating);
  };

  const handleStarHover = (starRating) => {
    setHoverRating(starRating);
  };

  const handleSubmitReview = async () => {
    if (rating === 0 || !reviewText.trim()) return;
    
    setIsSubmitting(true);
    
    try {
      const reviewData = {
        productId: productId,
        productName: productName || 'Unknown Product',
        userId: currentUser.uid,
        userName: currentUser?.displayName || currentUser.email || "Anonymous",
        rating: rating,
        reviewText: reviewText.trim(),
        images: [], // TODO: Add image upload functionality
        createdAt: serverTimestamp(),
        helpful: 0,
        verified: true,
        isPremium: false
      };

      // Save to Firebase
      const reviewsRef = collection(db, 'reviews');
      const docRef = await addDoc(reviewsRef, reviewData);
      
      // Create local review object with Firebase ID
      const newReview = {
        id: docRef.id,
        ...reviewData,
        createdAt: new Date() // Convert for local display
      };
      
      // Update local state
      setLocalReviews([newReview, ...localReviews]);
      setShowReviewForm(false);
      setRating(0);
      setReviewText('');
      setIsSubmitting(false);
      
      if (onReviewSubmitted) {
        onReviewSubmitted(newReview);
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      setIsSubmitting(false);
      alert('Failed to submit review. Please try again.');
    }
  };

    const averageRating = localReviews.length > 0
    ? localReviews.reduce((sum, review) => sum + review.rating, 0) / localReviews.length
    : 0;

  return (
    <div className="product-reviews-premium-container">
      {/* Ultra-Compact Premium Header */}
      <motion.div 
        className="reviews-premium-header-compact"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="reviews-header-compact-content">
          <div className="reviews-title-compact-section">
            <div className="reviews-title-with-icon">
              <FaGem className="reviews-gem-icon" />
              <h2 className="reviews-title-text">Customer Reviews</h2>
            </div>
            {/* Only show rating badge if there are actual reviews */}
            {localReviews.length > 0 && (
              <div className="reviews-rating-compact">
                <div className="reviews-stars-compact">
                  {[1, 2, 3, 4, 5].map((star) => {
                    const isFullStar = star <= Math.floor(averageRating);
                    const isHalfStar = star === Math.ceil(averageRating) && averageRating % 1 !== 0;
                    
                    return (
                      <div 
                        key={star}
                        className={`reviews-star-item ${isFullStar ? 'reviews-star-active' : 'reviews-star-inactive'}`}
                        style={{ position: 'relative' }}
                      >
                        {isHalfStar ? (
                          <>
                            {/* Background inactive star */}
                            <FaStar 
                              className="reviews-star-icon" 
                              style={{ 
                                color: 'rgba(44, 24, 16, 0.3)'
                              }}
                            />
                            {/* Half active star */}
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '50%',
                              height: '100%',
                              overflow: 'hidden'
                            }}>
                              <FaStar 
                                className="reviews-star-icon" 
                                style={{ 
                                  color: 'inherit',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0
                                }}
                              />
                            </div>
                          </>
                        ) : (
                          <FaStar className="reviews-star-icon" />
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="reviews-rating-info-section">
                  <span className="reviews-rating-score">{averageRating.toFixed(1)}</span>
                  <div className="reviews-rating-review-pill">
                    <span className="reviews-rating-review-text">{localReviews.length} {localReviews.length === 1 ? 'review' : 'reviews'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
          

        </div>
      </motion.div>

      {/* Review Form Modal */}
      <AnimatePresence>
        {showReviewForm && (
          <motion.div
            className="review-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowReviewForm(false)}
          >
            <motion.div
              className="review-modal"
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>Share Your Experience</h3>
                <button 
                  className="close-btn"
                  onClick={() => setShowReviewForm(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="modal-content">
                <div className="rating-section">
                  <label>Your Rating</label>
                  <div className="stars-input">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <FaStar
                        key={star}
                        className={`star-input ${star <= (hoverRating || rating) ? 'active' : ''}`}
                        onClick={() => handleStarClick(star)}
                        onMouseEnter={() => handleStarHover(star)}
                        onMouseLeave={() => handleStarHover(0)}
                        style={{
                          color: star <= (hoverRating || rating) ? '#FFD700' : '#E0E0E0',
                          filter: star <= (hoverRating || rating) ? 'drop-shadow(0 2px 8px rgba(255, 215, 0, 0.4))' : 'none'
                        }}
                      />
                    ))}
                  </div>
                  <span className="rating-text">
                    {rating === 0 ? 'Select rating' : `${rating} star${rating > 1 ? 's' : ''}`}
                  </span>
                </div>
                
                <div className="review-text-section">
                  <label>Your Review</label>
                  <textarea
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    placeholder="Share your thoughts about this product..."
                    rows={4}
                  />
                </div>
              </div>
              
              <div className="modal-actions">
                <button 
                  className="cancel-btn"
                  onClick={() => setShowReviewForm(false)}
                >
                  Cancel
                </button>
                <button
                  className="submit-btn"
                  onClick={handleSubmitReview}
                  disabled={rating === 0 || !reviewText.trim() || isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reviews List - No Filters */}

      {/* Loading State */}
      {isActuallyLoading && (
        <div className="reviews-loading">
          <div className="loading-spinner"></div>
          <p>Loading reviews...</p>
        </div>
      )}

      {/* Premium Reviews List */}
      <div className="reviews-premium-list">
        <AnimatePresence>
                    {!isActuallyLoading && localReviews.length === 0 && (
            <div className="review-beacon">
              <div className="beacon-light">💡</div>
              <div className="beacon-message">
                <h3>First Review</h3>
                <p>Light the way for others</p>
              </div>
            </div>
          )}
          {!isActuallyLoading && localReviews
            .filter(review => review.reviewText || (review.images && review.images.length > 0)) // Only show review cards with text or images
            .map((review, index) => (
            <motion.div
              key={review.id}
              className="review-premium-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
            >
              
              <div className="review-premium-header">
                <div className="review-premium-user-info">
                  <div className="review-premium-avatar">
                    {review.userName.charAt(0).toUpperCase()}
                  </div>
                  <div className="review-premium-user-details">
                    <h4 className="review-premium-username">{review.userName}</h4>
                    <div className="review-premium-meta">
                      <div className="review-premium-stars">
                        {[1, 2, 3, 4, 5].map((star) => {
                          const isFullStar = star <= Math.floor(review.rating);
                          const isHalfStar = star === Math.ceil(review.rating) && review.rating % 1 !== 0;
                          
                          return (
                            <div 
                              key={star}
                              className={`review-premium-star-item ${isFullStar ? 'review-premium-star-active' : 'review-premium-star-inactive'}`}
                              style={{ position: 'relative' }}
                            >
                              {isHalfStar ? (
                                <>
                                  {/* Background inactive star */}
                                  <FaStar 
                                    className="review-premium-star-icon" 
                                    style={{ 
                                      color: 'rgba(44, 24, 16, 0.3)'
                                    }}
                                  />
                                  {/* Half active star */}
                                  <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '50%',
                                    height: '100%',
                                    overflow: 'hidden'
                                  }}>
                                    <FaStar 
                                      className="review-premium-star-icon" 
                                      style={{ 
                                        color: 'inherit',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0
                                      }}
                                    />
                                  </div>
                                </>
                              ) : (
                                <FaStar className="review-premium-star-icon" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <span className="review-premium-date">
                        {review.createdAt.toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                

              </div>
              
              <div className="review-premium-content">
                <FaQuoteLeft className="review-premium-quote-left" />
                <p className="review-premium-text">{review.reviewText}</p>
                <FaQuoteRight className="review-premium-quote-right" />
              </div>
              
              {/* Review Images Section */}
              {review.images && review.images.length > 0 && (
                <div className="review-premium-images-section">
                  <div className="review-premium-images-grid">
                    {review.images.map((image, imgIndex) => {
                      // Debug: Log the image structure
                      
                      // Get the best webp image URL (prefer 480w for thumbnails)
                      let imageUrl = null;
                      if (image?.webp?.['400w']?.url) {
                        imageUrl = image.webp['400w'].url;
                      } else if (image?.webp?.['800w']?.url) {
                        imageUrl = image.webp['800w'].url;
                      } else if (image?.original?.url) {
                        imageUrl = image.original.url;
                      } else {
                        imageUrl = image;
                      }
                      
                      // Get full-size image for modal (prefer 1080w, fallback to original)
                      const fullSizeUrl = image?.webp?.['1080w']?.url || 
                                        image?.webp?.['768w']?.url || 
                                        image?.original?.url || 
                                        image;

                      
                      return (
                        <div 
                          key={imgIndex}
                          className="review-premium-image-thumbnail"
                          onClick={() => {
                            setSelectedImage(fullSizeUrl);
                            setShowImageModal(true);
                          }}
                        >
                          <img 
                            src={imageUrl} 
                            alt={`Review image ${imgIndex + 1}`}
                            className="review-premium-thumbnail-img"
                            loading="lazy"
                            onError={(e) => {
                              console.error('Image failed to load:', imageUrl);
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                          <div className="review-premium-image-fallback" style={{ display: 'none' }}>
                            <span>Image unavailable</span>
                          </div>
                          <div className="review-premium-image-overlay">
                            <span className="review-premium-image-text">Click to view</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      
      {/* Image Modal - Using Portal to render at document body level */}
      {showImageModal && selectedImage && createPortal(
        <AnimatePresence>
          <motion.div
            className="review-premium-image-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowImageModal(false)}
          >
            <motion.div
              className="review-premium-image-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="review-premium-image-modal-header">
                <button 
                  className="review-premium-image-modal-close"
                  onClick={() => setShowImageModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="review-premium-image-modal-content">
                <img 
                  src={selectedImage} 
                  alt="Review full size"
                  className="review-premium-image-modal-img"
                  loading="eager"
                  onError={(e) => {
                    console.error('Modal image failed to load:', selectedImage);
                    e.target.style.display = 'none';
                  }}
                  onLoad={() => {
                  }}
                />
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default ProductReviews;
